import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { UploadCloud } from 'lucide-react';
import { db } from './firebase';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { BUSINESS_TYPES, getBusinessType } from './businessTypes';

// Excel/CSV ফাইলের প্রথম সারির হেডার হুবহু এই নামে হতে হবে -- Cost Price/Selling
// Price ঐচ্ছিক, খালি রাখলে ০ ধরা হবে
const HEADERS = {
  main: 'Main Category', sub: 'Sub Category', name: 'Product Name',
  cost: 'Cost Price', sell: 'Selling Price', qty: 'Quantity'
};

const findKey = (row, target) => {
  const key = Object.keys(row).find((k) => k.trim().toLowerCase() === target.toLowerCase());
  return key ? String(row[key] ?? '').trim() : '';
};

const ImportData = () => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) setFileName(file.name);
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files[0];
    if (!file) return alert('Please choose an Excel (.xlsx) or .csv file first!');

    setUploading(true);
    setResult(null);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (rows.length === 0) throw new Error('The file has no rows.');

      // Firestore-এ ইতিমধ্যে থাকা প্রোডাক্টগুলো (নাম + Main Category মিলিয়ে ডুপ্লিকেট বাদ দেওয়ার জন্য)
      const existingSnap = await getDocs(collection(db, 'products'));
      const existingKeys = new Set(
        existingSnap.docs.map((d) => {
          const p = d.data();
          return `${(p.name || '').toLowerCase().trim()}__${getBusinessType(p)}`;
        })
      );

      let added = 0, skippedExisting = 0, skippedInvalid = 0;
      const invalidRows = [];

      for (const row of rows) {
        const mainCategory = findKey(row, HEADERS.main);
        const subCategory = findKey(row, HEADERS.sub);
        const productName = findKey(row, HEADERS.name);
        const costPrice = Number(findKey(row, HEADERS.cost)) || 0;
        const sellingPrice = Number(findKey(row, HEADERS.sell)) || 0;
        const quantity = Math.max(0, Number(findKey(row, HEADERS.qty)) || 0);

        if (!productName || !BUSINESS_TYPES.includes(mainCategory)) {
          skippedInvalid++;
          invalidRows.push(`${productName || '(no name)'} — Main Category "${mainCategory}" not recognized`);
          continue;
        }

        const key = `${productName.toLowerCase().trim()}__${mainCategory}`;
        if (existingKeys.has(key)) {
          skippedExisting++;
          continue;
        }

        await addDoc(collection(db, 'products'), {
          name: productName,
          businessType: mainCategory,
          category: subCategory,
          price: sellingPrice,
          costPrice,
          stock: quantity,
          createdAt: serverTimestamp()
        });
        existingKeys.add(key); // একই ফাইলে একই নাম দুইবার থাকলে দ্বিতীয়বার আটকাবে
        added++;
      }

      setResult({ added, skippedExisting, skippedInvalid, invalidRows });
    } catch (err) {
      alert('Import failed: ' + err.message);
    }
    setUploading(false);
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      <div className="white-box" style={{ width: '550px', textAlign: 'center', border: '2px dashed #5100e1' }}>
        <UploadCloud size={50} color="#5100e1" style={{ marginBottom: '15px' }} />
        <h2 style={{ color: '#5100e1', marginBottom: '10px' }}>Bulk Product Import</h2>
        <p style={{ color: '#a3aed0', fontSize: '14px', marginBottom: '10px' }}>
          The file's first row must have these column headers:
        </p>
        <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#5100e1', marginBottom: '4px' }}>
          Main Category | Sub Category | Product Name
        </p>
        <p style={{ fontSize: '12px', color: '#888', marginBottom: '20px' }}>
          + optionally: Cost Price | Selling Price | Quantity
        </p>
        <p style={{ color: '#a3aed0', fontSize: '12px', marginBottom: '25px' }}>
          Main Category must be one of: {BUSINESS_TYPES.join(', ')}. Cost Price/Selling Price/Quantity are optional —
          leave any of them blank and it still uploads fine (blank means 0). Fill them in and they'll be used as
          the product's starting cost, price, and stock. If a product with the same name and Main Category
          already exists, it won't be uploaded again.
        </p>

        <div style={{ border: '2px dotted #e0e5f2', padding: '40px', borderRadius: '20px', background: '#f8faff' }}>
          <input type="file" accept=".xlsx,.xls,.csv" ref={fileInputRef} onChange={handleFile} style={{ cursor: 'pointer' }} />
          <p style={{ fontSize: '12px', color: '#888', marginTop: '10px' }}>
            {fileName ? `Selected: ${fileName}` : 'Supported: .xlsx, .csv'}
          </p>
        </div>

        <button
          className="btn-primary"
          style={{ marginTop: '25px', width: '200px', opacity: uploading ? 0.7 : 1 }}
          onClick={handleUpload}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload Now'}
        </button>

        {result && (
          <div style={{ marginTop: '20px', padding: '15px', borderRadius: '12px', background: '#f4f7fe', textAlign: 'left', fontSize: '13px' }}>
            <p style={{ margin: '4px 0', color: '#218c61', fontWeight: 'bold' }}>✅ {result.added} product(s) added</p>
            {result.skippedExisting > 0 && <p style={{ margin: '4px 0', color: '#666' }}>⏭️ {result.skippedExisting} skipped (already exist)</p>}
            {result.skippedInvalid > 0 && (
              <div style={{ marginTop: '8px' }}>
                <p style={{ margin: '4px 0', color: '#e74c3c', fontWeight: 'bold' }}>⚠️ {result.skippedInvalid} row(s) skipped (invalid data):</p>
                <ul style={{ margin: '4px 0', paddingLeft: '20px', color: '#e74c3c', maxHeight: '120px', overflowY: 'auto' }}>
                  {result.invalidRows.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportData;
